import {
    SearchAudioMediaTypeEnum,
    SearchAudioSortByEnum,
    type SearchAudioSortOrderEnum,
    type SearchSimilarApiGetSimilarAudioItemsRequest,
    type WebSearchApiSearchAudioRequest,
    type WebSearchApiSearchGraphicRequest,
    type WebSearchApiSearchVideoRequest,
} from '@videoblocks/search-gateway-client';

import { Rule, SearchUrlParamKeys } from './types';

export type ImageRuleKey = Required<keyof WebSearchApiSearchGraphicRequest>;
export type AudioRuleKey = Required<keyof WebSearchApiSearchAudioRequest>;
export type VideoRuleKey = Required<keyof WebSearchApiSearchVideoRequest>;
export type SimilarSearchAudioRuleKey = keyof SearchSimilarApiGetSimilarAudioItemsRequest;

export const paginationRule: Rule<string | string[], number> = {
    searchKey: 'page',
    transform: (v) => {
        const n = parseInt(Array.isArray(v) ? v[0] : v, 10);
        return Number.isFinite(n) && n > 0 ? n : 1;
    },
};

const FRAME_RATE_VALUE_MAP: Record<string, string> = {
    fps24: '23.98/24',
    fps25: '25',
    fps30: '29.97/30',
    fps50: '50',
    fps60: '59.54/60',
};

// TODO: map searchKeys to proper api request keys

export const imageRules = {
    pageNumber: paginationRule,
    mediaType: {
        searchKey: SearchUrlParamKeys.MEDIA_TYPE,
        transform: (v) => {
            if (!v) return undefined;
            const mediaType = Array.isArray(v) ? v[0] : v;
            return ['Photos', 'Snapshots', 'Vectors', 'Illustrations'].includes(mediaType) ? mediaType : undefined;
        },
    } satisfies Rule<string | string[] | undefined, string | undefined>,
    modelReleased: {
        searchKey: 'mediaDetails',
        transform: (v) => {
            if (!v) return false;
            return v.includes('talentReleased') ? true : undefined;
        },
    } satisfies Rule<string | undefined, boolean | undefined>,
    propertyReleased: {
        searchKey: 'mediaDetails',
        transform: (v) => {
            if (!v) return false;
            return v.includes('propertyReleased') ? true : undefined;
        },
    } satisfies Rule<string | undefined, boolean | undefined>,
    hasTransparency: {
        searchKey: 'fileTypes',
        transform: (v) => {
            return v === 'true' ? true : v === 'false' ? false : undefined;
        },
    } satisfies Rule<string | undefined, boolean | undefined>,
    orientation: {
        searchKey: 'orientation',
        transform: (v) => {
            if (!v) return undefined;
            const orientation = Array.isArray(v) ? v[0] : v;
            return ['Landscape', 'Portrait', 'Square'].includes(orientation) ? orientation : undefined;
        },
    } satisfies Rule<string | string[] | undefined, string | undefined>,
    usageRights: {
        searchKey: 'usageRights',
        transform: (v) => {
            if (!v) return 'All';
            if (v.includes('all')) return 'All';
            if (v.includes('commercial')) return 'Commercial';
            if (v.includes('editorial')) return 'Editorial';
        },
    } satisfies Rule<string | undefined, string | undefined>,
    categories: {
        searchKey: 'categories',
        transform: (v) => {
            if (!v) return undefined;
            if (typeof v === 'string') {
                v = v.split('_');
            }
            return v;
        },
    } satisfies Rule<string | string[] | undefined, string | string[] | undefined>,
    searchTerm: {
        searchKey: 'searchTerm',
    },
    forceRefresh: {
        searchKey: 'forceRefresh',
    },
    color: {
        searchKey: 'color',
    },
    numResults: {
        searchKey: 'numResults',
    },
    sortBy: {
        searchKey: 'sortBy',
    },
    collapsedSetId: { searchKey: 'collapsedSetId' },
    excludedStockblocksIds: {
        searchKey: 'excludedStockblocksIds',
    },
} as const;

export const audioRules = {
    pageNumber: paginationRule,
    sortBy: {
        searchKey: 'sort',
        transform: (v) => {
            if (!v) return SearchAudioSortByEnum.MostRelevant;
            const sort = Array.isArray(v) ? v[0] : v;
            if (sort.includes('asc') || sort.includes('desc')) {
                return sort.split('_')[0] as SearchAudioSortByEnum;
            }
            return sort as SearchAudioSortByEnum;
        },
    } satisfies Rule<string | string[] | undefined, SearchAudioSortByEnum>,
    sortOrder: {
        searchKey: 'sort',
        transform: (v) => {
            if (!v) return null;
            const sort = Array.isArray(v) ? v[0] : v;
            if (sort.includes('asc') || sort.includes('desc')) {
                return sort.split('_')[1] as SearchAudioSortOrderEnum;
            }
        },
    } satisfies Rule<string | string[] | undefined, SearchAudioSortOrderEnum | null | undefined>,
    mediaType: {
        searchKey: SearchUrlParamKeys.MEDIA_TYPE,
        transform: (v) => {
            const mediaType: 'music' | 'sound-effects' = Array.isArray(v) ? v[0] : v;
            const map = {
                music: SearchAudioMediaTypeEnum.Music,
                'sound-effects': SearchAudioMediaTypeEnum.Sfx,
            };
            return map[mediaType];
        },
    } satisfies Rule<'music' | 'sound-effects' | ('music' | 'sound-effects')[], SearchAudioMediaTypeEnum>,
    musicMoods: {
        searchKey: 'moods',
        transform: (v) => {
            if (!v) return undefined;
            const isArray = Array.isArray(v);
            if (!v || (isArray && !v.length)) return undefined;
            const arr = isArray ? v : String(v).split('_');
            const returnVal = arr.map((val) => parseInt(val, 10)).filter((num) => !isNaN(num));
            return returnVal;
        },
    } satisfies Rule<string | string[] | undefined, number[] | undefined>,
    musicGenres: {
        searchKey: 'genres',
        transform: (v) => {
            if (!v) return undefined;
            const isArray = Array.isArray(v);
            if (!v || (isArray && !v.length)) return undefined;
            const arr = isArray ? v : String(v).split('_');
            const returnVal = arr.map((val) => parseInt(val, 10)).filter((num) => !isNaN(num));
            return returnVal;
        },
    } satisfies Rule<string | string[] | undefined, number[] | undefined>,
    musicInstruments: {
        searchKey: 'instruments',
        transform: (v) => {
            if (!v) return undefined;
            const isArray = Array.isArray(v);
            if (!v || (isArray && !v.length)) return undefined;
            const arr = isArray ? v : String(v).split('_');
            const returnVal = arr.map((val) => parseInt(val, 10)).filter((num) => !isNaN(num));
            return returnVal;
        },
    } satisfies Rule<string | string[] | undefined, number[] | undefined>,
    hasVocals: {
        searchKey: 'vocals',
        transform: (v) => {
            return v === 'true' ? true : v === 'false' ? false : undefined;
        },
    } satisfies Rule<string | undefined, boolean | undefined>,
    minDuration: {
        searchKey: 'duration_min',
        transform: (v) => {
            if (!v) return undefined;
            const n = parseInt(Array.isArray(v) ? v[0] : v, 10);
            return Number.isFinite(n) && n >= 0 ? n : 0;
        },
    } satisfies Rule<string | string[] | undefined, number | undefined>,
    maxDuration: {
        searchKey: 'duration_max',
        transform: (v) => {
            if (!v) return undefined;
            const n = parseInt(Array.isArray(v) ? v[0] : v, 10);
            if (n > 240) return 240;
            return Number.isFinite(n) && n >= 0 ? n : 240;
        },
    } satisfies Rule<string | string[] | undefined, number | undefined>,
    sfxCategories: {
        searchKey: 'categories',
        transform: (v) => {
            const isArray = Array.isArray(v);
            if (!v || (isArray && !v.length)) return undefined;
            const arr = isArray ? v : String(v).split('_');
            const returnVal = arr.map((val) => parseInt(val, 10)).filter((num) => !isNaN(num));
            return returnVal;
        },
    } satisfies Rule<string | string[] | undefined, number[] | undefined>,
    searchTerm: {
        searchKey: 'searchTerm',
    },
    forceRefresh: {
        searchKey: 'forceRefresh',
    },
    numResults: {
        searchKey: 'numResults',
    },
    excludedStockblocksIds: {
        searchKey: 'excludedStockblocksIds',
    },
    collapsedSetId: {
        searchKey: 'collapsedSetId',
    },
    bpmMin: {
        searchKey: 'bpmMin',
    },
    bpmMax: {
        searchKey: 'bpmMax',
    },
    artistId: {
        searchKey: SearchUrlParamKeys.ARTIST_IDS,
        transform: (v) => {
            if (!v) return undefined;
            const arr = Array.isArray(v) ? v : String(v).split('_');
            return arr.length ? arr : undefined;
        },
    } satisfies Rule<string | string[] | undefined, string[] | undefined>,
    algorithm: {
        searchKey: 'algorithm',
        transform: (v) => {
            if (!v) return '';
            return Array.isArray(v) ? v[0] : v;
        },
    } satisfies Rule<string | string[] | undefined, string>,
} as const;

export const videoRules = {
    pageNumber: paginationRule,
    mediaType: {
        searchKey: SearchUrlParamKeys.MEDIA_TYPE,
        transform: (v) => {
            const mediaType = Array.isArray(v) ? v[0] : v;
            return ['All', 'Footage', 'Templates', 'Backgrounds'].includes(mediaType) ? mediaType : undefined;
        },
    } satisfies Rule<string | string[], string | undefined>,
    resolutions: {
        searchKey: 'resolution',
        transform: (v) => {
            const resolution = Array.isArray(v) ? v[0] : v;
            return ['HD', '4K'].includes(resolution) ? resolution : '';
        },
    } satisfies Rule<string | string[], string | undefined>,
    frameRates: {
        searchKey: 'frameRate',
        transform: (v) => {
            if (!v) return undefined;
            const items: string[] = Array.isArray(v) ? v : v.split('_');
            return items.map((item) => FRAME_RATE_VALUE_MAP[item.trim()] ?? item);
        },
    } satisfies Rule<string | string[] | undefined, string[] | undefined>,
    modelReleased: {
        searchKey: 'mediaDetails',
        transform: (v) => {
            if (!v) return false;
            return v.includes('talentReleased') ? true : undefined;
        },
    } satisfies Rule<string | undefined, boolean | undefined>,
    usageRights: {
        searchKey: 'usageRights',
        transform: (v) => {
            if (!v) return 'All';
            if (v.includes('all')) return 'All';
            if (v.includes('commercial')) return 'Commercial';
            if (v.includes('editorial')) return 'Editorial';
        },
    } satisfies Rule<string | undefined, string | undefined>,
    propertyReleased: {
        searchKey: 'mediaDetails',
        transform: (v) => {
            if (!v) return false;
            return v.includes('propertyReleased') ? true : undefined;
        },
    } satisfies Rule<string | undefined, boolean | undefined>,
    categories: {
        searchKey: 'categories',
        transform: (v) => {
            if (!v) return undefined;
            if (typeof v === 'string') {
                v = v.split('_');
            }
            return v;
        },
    } satisfies Rule<string | string[] | undefined, string[] | undefined>,
    searchTerm: {
        searchKey: 'searchTerm',
    },
    forceRefresh: {
        searchKey: 'forceRefresh',
    },
    numResults: {
        searchKey: 'numResults',
    },
    sortBy: {
        searchKey: 'sortBy',
    },
    collapsedSetId: {
        searchKey: 'collapsedSetId',
    },
    excludedStockblocksIds: {
        searchKey: 'excludedStockblocksIds',
    },
    minDuration: {
        searchKey: 'minDuration',
    },
    maxDuration: { searchKey: 'maxDuration' },
    templateType: {
        searchKey: 'templateType',
    },
} as const;

export const similarSearchAudioRules = {
    contentId: {
        searchKey: 'contentId',
    },
    numResults: {
        searchKey: 'numResults',
    },
    pageNumber: paginationRule,
} as const;

export const defaultRules = { pageNumber: paginationRule } as const;

// export const imageUrlParamToSearchGatewayParam: Record<string, string> = Object.fromEntries(
//     Object.entries(imageRules).map(([key, rule]) => [rule.searchKey, key]),
// );

// export const audioUrlParamToSearchGatewayParam: Record<string, string> = Object.fromEntries(
//     Object.entries(audioRules).map(([key, rule]) => [rule.searchKey, key]),
// );

// export const videoUrlParamToSearchGatewayParam: Record<string, string> = Object.fromEntries(
//     Object.entries(videoRules).map(([key, rule]) => [rule.searchKey, key]),
// );

// export const similarSearchAudioUrlParamToSearchGatewayParam: Record<string, string> = Object.fromEntries(
//     Object.entries(similarSearchAudioRules).map(([key, rule]) => [rule.searchKey, key]),
// );

export const Rules = {
    image: imageRules,
    audio: audioRules,
    video: videoRules,
    similarSearchAudio: similarSearchAudioRules,
};

export default Rules;
